import { useState } from 'react';
import { PageHeader, Badge } from './shared';
import { Icon } from './ui/icons';

interface SettingsSection {
  id: string;
  title: string;
  description: string;
  icon: string;
}

const settingsSections: SettingsSection[] = [
  {
    id: 'general',
    title: 'General Settings',
    description: 'Basic dashboard preferences and configuration',
    icon: 'settings'
  },
  {
    id: 'integrations',
    title: 'Integrations',
    description: 'Connect security tools and compliance platforms',
    icon: 'link'
  },
  {
    id: 'notifications',
    title: 'Notifications',
    description: 'Configure alerts and notification preferences',
    icon: 'bell'
  },
  {
    id: 'security',
    title: 'Security & Access',
    description: 'User access controls and authentication settings',
    icon: 'shield'
  },
  {
    id: 'data',
    title: 'Data & Export',
    description: 'Data retention, backups, and export configuration',
    icon: 'download'
  },
  {
    id: 'billing',
    title: 'Billing & Usage',
    description: 'Subscription plans, usage metrics, and billing',
    icon: 'credit-card'
  }
];

export default function Settings() {
  const [selectedSection, setSelectedSection] = useState('general');
  
  const renderGeneralSettings = () => (
    <div className="space-y-8">
      <div>
        <h3 className="font-display font-bold text-ink text-[18px] mb-4">Dashboard Preferences</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display font-semibold text-ink text-[14px]">Dark Mode</div>
              <div className="font-display text-[13px] text-ink/60">Switch to dark theme</div>
            </div>
            <button className="w-12 h-6 bg-gray-200 rounded-full relative transition">
              <div className="w-5 h-5 bg-white rounded-full absolute left-0.5 top-0.5 transition" />
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display font-semibold text-ink text-[14px]">Auto-refresh Data</div>
              <div className="font-display text-[13px] text-ink/60">Automatically update dashboard data</div>
            </div>
            <button className="w-12 h-6 bg-primary-500 rounded-full relative transition">
              <div className="w-5 h-5 bg-white rounded-full absolute right-0.5 top-0.5 transition" />
            </button>
          </div>
          
          <div>
            <div className="font-display font-semibold text-ink text-[14px] mb-2">Refresh Interval</div>
            <select className="font-display text-[14px] text-ink border border-[#F3E4C8] rounded-[6px] px-3 py-2 bg-white">
              <option value="30">30 seconds</option>
              <option value="60" selected>1 minute</option>
              <option value="300">5 minutes</option>
              <option value="600">10 minutes</option>
            </select>
          </div>
        </div>
      </div>

      <div>
        <h3 className="font-display font-bold text-ink text-[18px] mb-4">Organization</h3>
        <div className="space-y-4">
          <div>
            <label className="font-display font-semibold text-ink text-[14px] mb-2 block">Organization Name</label>
            <input 
              type="text" 
              defaultValue="Captain & Co." 
              className="w-full font-display text-[14px] text-ink border border-[#F3E4C8] rounded-[6px] px-3 py-2"
            />
          </div>
          
          <div>
            <label className="font-display font-semibold text-ink text-[14px] mb-2 block">Default Timezone</label>
            <select className="w-full font-display text-[14px] text-ink border border-[#F3E4C8] rounded-[6px] px-3 py-2 bg-white">
              <option value="UTC">UTC (Coordinated Universal Time)</option>
              <option value="US/Pacific" selected>US/Pacific (PST/PDT)</option>
              <option value="US/Eastern">US/Eastern (EST/EDT)</option>
              <option value="Europe/London">Europe/London (GMT/BST)</option>
              <option value="Europe/Paris">Europe/Paris (CET/CEST)</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderIntegrationsSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          { name: 'GitHub', status: 'connected', color: '#24292f', description: 'Source code and security scanning' },
          { name: 'AWS', status: 'connected', color: '#FF9900', description: 'Cloud infrastructure monitoring' },
          { name: 'Azure AD', status: 'connected', color: '#0078d4', description: 'Identity and access management' },
          { name: 'CrowdStrike', status: 'pending', color: '#e01e37', description: 'Endpoint detection and response' },
          { name: 'Splunk', status: 'disconnected', color: '#000000', description: 'Security information and event management' },
          { name: 'Okta', status: 'disconnected', color: '#007DC1', description: 'Identity and access management' }
        ].map(integration => (
          <div key={integration.name} className="p-4 border border-[#F3E4C8] rounded-[8px] bg-white">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div 
                  className="w-8 h-8 rounded-[6px] grid place-items-center text-white font-bold text-[12px]"
                  style={{ backgroundColor: integration.color }}
                >
                  {integration.name.charAt(0)}
                </div>
                <div>
                  <div className="font-display font-semibold text-ink text-[14px]">{integration.name}</div>
                  <div className="font-display text-[12px] text-ink/60">{integration.description}</div>
                </div>
              </div>
              <Badge 
                tone={integration.status === 'connected' ? 'ok' : integration.status === 'pending' ? 'warn' : 'muted'}
                dot
              >
                {integration.status}
              </Badge>
            </div>
            <button className="w-full h-8 px-3 rounded-[6px] border border-[#F3E4C8] bg-white font-display font-semibold text-[12px] text-ink hover:border-ink/40">
              {integration.status === 'connected' ? 'Configure' : 'Connect'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderCurrentSection = () => {
    switch (selectedSection) {
      case 'general':
        return renderGeneralSettings();
      case 'integrations':
        return renderIntegrationsSettings();
      case 'notifications':
        return (
          <div className="p-8 text-center">
            <Icon name="bell" size={48} className="text-ink/40 mx-auto mb-4" />
            <h3 className="font-display font-bold text-ink text-[18px] mb-2">Notification Settings</h3>
            <p className="font-display text-[14px] text-ink/60">Configure alert preferences and notification channels.</p>
          </div>
        );
      case 'security':
        return (
          <div className="p-8 text-center">
            <Icon name="shield" size={48} className="text-ink/40 mx-auto mb-4" />
            <h3 className="font-display font-bold text-ink text-[18px] mb-2">Security & Access</h3>
            <p className="font-display text-[14px] text-ink/60">Manage user access controls and authentication settings.</p>
          </div>
        );
      case 'data':
        return (
          <div className="p-8 text-center">
            <Icon name="download" size={48} className="text-ink/40 mx-auto mb-4" />
            <h3 className="font-display font-bold text-ink text-[18px] mb-2">Data & Export</h3>
            <p className="font-display text-[14px] text-ink/60">Configure data retention policies and export options.</p>
          </div>
        );
      case 'billing':
        return (
          <div className="space-y-6">
            <div className="p-6 border border-[#F3E4C8] rounded-[10px] bg-white">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-display font-bold text-ink text-[18px]">Current Plan</h3>
                  <p className="font-display text-[14px] text-ink/60">Professional Plan</p>
                </div>
                <Badge tone="draft" className="text-[12px]">Active</Badge>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center">
                  <div className="font-display font-bold text-[24px] text-ink">5</div>
                  <div className="font-display text-[12px] text-ink/60">Projects</div>
                </div>
                <div className="text-center">
                  <div className="font-display font-bold text-[24px] text-ink">12</div>
                  <div className="font-display text-[12px] text-ink/60">Repositories</div>
                </div>
                <div className="text-center">
                  <div className="font-display font-bold text-[24px] text-ink">25</div>
                  <div className="font-display text-[12px] text-ink/60">Controls</div>
                </div>
              </div>
              
              <button className="w-full h-10 px-4 rounded-[8px] bg-[#FA500E] text-white font-display font-bold text-[14px] hover:opacity-90">
                Upgrade Plan
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title="Settings"
        subtitle="Configure dashboard preferences and integrations."
      />

      <div className="flex gap-8">
        {/* Settings Navigation */}
        <div className="w-64 flex-shrink-0">
          <nav className="space-y-1">
            {settingsSections.map(section => (
              <button
                key={section.id}
                onClick={() => setSelectedSection(section.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-[8px] text-left transition ${
                  selectedSection === section.id
                    ? 'bg-primary-100 text-primary-700 font-semibold'
                    : 'hover:bg-gray-100 text-ink'
                }`}
              >
                <Icon name={section.icon as any} size={16} />
                <div>
                  <div className="font-display text-[14px]">{section.title}</div>
                  <div className="font-display text-[12px] text-ink/60">{section.description}</div>
                </div>
              </button>
            ))}
          </nav>
        </div>

        {/* Settings Content */}
        <div className="flex-1">
          <div className="bg-white rounded-[10px] border border-[#F3E4C8] p-6">
            {renderCurrentSection()}
          </div>
        </div>
      </div>
    </div>
  );
}